import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import { userService } from '@/services/userService';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';

const UserPasswordSetDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { userId, onClose } = props;

  const { record, validation, fields, onFieldChange, doClose, pickerDataControllers, doValidate } = useFormController(
    props as FormControllerProps
  );

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleSave = () => {
    if (doValidate()) {
      userService
        .setPassword(userId, record)
        .then(() => {
          showNotification(t('messages.password_set_success'), 'success');
          close({
            dataChanged: true,
            action: 'update',
          });
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.log(e));
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog();
  };

  return (
    <DynamicDialog open maxWidth="xs" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('titles.set_password')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            fieldNames={['password', 'password_confirm']}
            record={record}
            validation={validation}
            fields={fields}
            mode="update"
            onFieldChange={handleFieldChange}
            pickerDataControllers={pickerDataControllers}
            columns={1}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        <DialogActionButtonSave variant="contained" onClick={handleSave} />
      </DialogActions>
    </DynamicDialog>
  );
};

export default UserPasswordSetDialog;
